
export function debounce(func, delay) {
    let timeoutId;
    return function () {
        const context = this;
        const args = arguments;
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(context, args), delay);
    };
}

// Advanced Search Query Parser
// Supports: AND (default), OR/|, NOT/-, "exact phrases", (grouping)
export function parseSearchQuery(query) {
    const tokens = [];
    let i = 0;
    while (i < query.length) {
        if (query[i] === ' ') { i++; continue; }
        if (query[i] === '(') { tokens.push({ type: 'LPAREN' }); i++; continue; }
        if (query[i] === ')') { tokens.push({ type: 'RPAREN' }); i++; continue; }
        if (query[i] === '-' && (tokens.length === 0 || ['LPAREN', 'OR'].includes(tokens[tokens.length - 1]?.type))) {
            tokens.push({ type: 'NOT' }); i++; continue;
        }
        if (query[i] === '|') { tokens.push({ type: 'OR' }); i++; continue; }
        if (query[i] === '"') {
            let end = query.indexOf('"', i + 1);
            if (end === -1) end = query.length;
            tokens.push({ type: 'PHRASE', value: query.slice(i + 1, end).toLowerCase() });
            i = end + 1; continue;
        }
        // Word
        let word = '';
        while (i < query.length && !/[\s()|"]/.test(query[i])) {
            word += query[i]; i++;
        }
        if (word.toUpperCase() === 'OR') tokens.push({ type: 'OR' });
        else if (word.toUpperCase() === 'NOT') tokens.push({ type: 'NOT' });
        else tokens.push({ type: 'WORD', value: word.toLowerCase() });
    }
    return tokens;
}

export function buildSearchAST(tokens) {
    let pos = 0;

    function parseExpr() {
        return parseOr();
    }

    function parseOr() {
        let left = parseAnd();
        while (pos < tokens.length && tokens[pos]?.type === 'OR') {
            pos++;
            let right = parseAnd();
            left = { type: 'OR', left, right };
        }
        return left;
    }

    function parseAnd() {
        let left = parseUnary();
        while (pos < tokens.length &&
            tokens[pos]?.type !== 'OR' &&
            tokens[pos]?.type !== 'RPAREN' &&
            tokens[pos]) {
            let right = parseUnary();
            if (right) left = { type: 'AND', left, right };
            else break;
        }
        return left;
    }

    function parseUnary() {
        if (tokens[pos]?.type === 'NOT') {
            pos++;
            return { type: 'NOT', child: parseUnary() };
        }
        return parsePrimary();
    }

    function parsePrimary() {
        if (tokens[pos]?.type === 'LPAREN') {
            pos++;
            let expr = parseExpr();
            if (tokens[pos]?.type === 'RPAREN') pos++;
            return expr;
        }
        if (tokens[pos]?.type === 'PHRASE') {
            return { type: 'PHRASE', value: tokens[pos++].value };
        }
        if (tokens[pos]?.type === 'WORD') {
            return { type: 'WORD', value: tokens[pos++].value };
        }
        return null;
    }

    return parseExpr();
}

export function evaluateSearchAST(ast, objName, altName, bm) {
    if (!ast) return true;

    const matchWord = (word) => {
        const objWords = objName.split(' ');
        const altWords = altName?.split(' ') || [];
        // Substring match first
        if (objName.includes(word) || altName?.includes(word)) return true;
        // Beider-Morse fuzzy match
        return objWords.some(w => bm.isMatch(w, word)) ||
            altWords.some(w => bm.isMatch(w, word));
    };

    switch (ast.type) {
        case 'AND': return evaluateSearchAST(ast.left, objName, altName, bm) && evaluateSearchAST(ast.right, objName, altName, bm);
        case 'OR': return evaluateSearchAST(ast.left, objName, altName, bm) || evaluateSearchAST(ast.right, objName, altName, bm);
        case 'NOT': return !evaluateSearchAST(ast.child, objName, altName, bm);
        case 'WORD': return matchWord(ast.value);
        case 'PHRASE': return objName.includes(ast.value) || altName?.includes(ast.value);
        default: return true;
    }
}

export function matchesAdvancedSearch(search, objName, altName, bm) {
    // Strip 'de' and 'del' particles to match PHP logic
    // This allows queries like "creme de provence" to match items named "creme provence"
    let cleanedSearch = search
        .replace(/\bde\b/gi, ' ')
        .replace(/\bdel\b/gi, ' ');

    const tokens = parseSearchQuery(cleanedSearch);
    const ast = buildSearchAST(tokens);
    return evaluateSearchAST(ast, objName.toLowerCase(), altName?.toLowerCase(), bm);
}
